import {noop} from '@benchling/js-utils';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, {Ref} from 'react';

import * as benchlingUtil from 'BenchlingUtil';
import * as MediocreConstants from 'notebook/mediocre/MediocreConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {AttachDropdownItem, OptionHeight} from 'ui-library/attach-dropdown/AttachDropdownConstants';
// @ts-expect-error
// eslint-disable-next-line unused-imports/no-unused-imports
import AttachDropdownOption from 'ui-library/attach-dropdown/AttachDropdownOption';
import {BasicEvent} from 'util/Types';

/**
 * Components for the menu of options that are rendered by AttachDropdown. AttachDropdownOptionsMenu renders a
 * list of AttachDropdownOptionContainers, one for each result provided to it.
 */

const _optionHeightClasses = {
  [OptionHeight.MEDIUM]: 'attachDropdown-option--medium',
  [OptionHeight.LARGE]: 'attachDropdown-option--large',
  [OptionHeight.XLARGE]: 'attachDropdown-option--xl',
};

export type SelectAttachment = (
  id: string | number,
  name: string,
  url: string | null,
  item: object,
  internalLink: MediocreConstants.InternalLink | undefined,
  index: number
) => void;

interface AttachDropdownOptionContainerProps {
  index: number;
  isHovered: boolean;
  children: JSX.Element;
  // Result is required unless you provide a custom onClick handler
  result: AttachDropdownItem | null;
  selectAttachment: SelectAttachment;
  // If provided, this custom click handler will be used instead of `selectAttachment`
  onClick?: (() => void) | null;
  onHover: (index: number) => void;
  height: OptionHeight;
}

/*
We use refs to AttachDropdownOptionContainer to handle hover behavior, and refs cannot be attached to
functional components, so we have to keep it as a class.
 */
export class AttachDropdownOptionContainer extends React.PureComponent<AttachDropdownOptionContainerProps> {
  static propTypes = {
    index: PropTypes.number.isRequired,
    isHovered: PropTypes.bool.isRequired,
    children: PropTypes.node.isRequired,
    result: PropTypes.object,
    selectAttachment: PropTypes.func.isRequired,
    onClick: PropTypes.func,
    onHover: PropTypes.func.isRequired,
    height: PropTypes.oneOf(Object.values(OptionHeight)).isRequired,
  };

  onMouseOver = (): void => {
    this.props.onHover(this.props.index);
  };

  onClick = (): void => {
    if (this.props.onClick) {
      this.props.onClick();
    } else {
      // One of onClick or result must be non-null
      const result = this.props.result!;
      const id = result.item_id;
      const relativeUrl = result.item.editURL || result.item.editUrl || result.item.url;
      const url = relativeUrl ? benchlingUtil.pathToURL(relativeUrl) : null;
      this.props.selectAttachment(id, result.name, url, result.item, result.internalLink, this.props.index);
    }
  };

  onMouseDown = (e: BasicEvent): void => {
    // One use scenario (see BioentityLinkInput) for AttachDropdown will hide the dropdown
    // if focus is removed from some input element. This behavior is controlled by listening to onBlur
    // events. If the user clicks inside of an AttachDropdownOption, we should prevent onMouseDown from
    // stealing focus from activeElement. If onMouseDown is instead allowed to steal focus, then the
    // component listening to onBlur will hide AttachDropdown, causing this components onClick handler to
    // never be fired.
    e.preventDefault();
  };

  render(): JSX.Element {
    return (
      <li
        // TODO: Pass in the selected option ID so we can set aria-selected
        // eslint-disable-next-line jsx-a11y/role-has-required-aria-props
        role="option"
        onClick={this.onClick}
        onMouseDown={this.onMouseDown}
        onMouseOver={this.onMouseOver}
        {...getTestProps({component: 'AttachDropdownOptionContainer', element: 'root'})}
      >
        <div
          className={classNames('attachDropdown-option', _optionHeightClasses[this.props.height], {
            'is-hovered': this.props.isHovered,
          })}
          {...getTestProps({component: 'AttachDropdownOptionContainer', element: 'option'})}
        >
          {this.props.children}
        </div>
      </li>
    );
  }
}

export interface RenderAttachDropdownOptionInfo {
  optionContent: JSX.Element;
  hoverable: boolean;
  clickable: boolean;
  customHeight?: OptionHeight;
}

interface AttachDropdownMenuProps {
  results: Array<AttachDropdownItem>;
  getRenderOptionInfo: (result: AttachDropdownItem) => RenderAttachDropdownOptionInfo;
  selectAttachment: SelectAttachment;
  hoverIndex: number;
  setHoverIndex: (index: number) => void;
  refs: Array<Ref<AttachDropdownOptionContainer>>;
  height: OptionHeight;
}

function AttachDropdownOptionsMenu({
  results,
  getRenderOptionInfo,
  selectAttachment,
  hoverIndex,
  setHoverIndex,
  refs,
  height,
}: AttachDropdownMenuProps): JSX.Element {
  return (
    <>
      {results.map((result, index) => {
        const {optionContent, hoverable, clickable, customHeight} = getRenderOptionInfo(result);
        return (
          <AttachDropdownOptionContainer
            key={index}
            ref={refs[index]}
            index={index}
            isHovered={hoverable ? hoverIndex === index : false}
            height={customHeight || height}
            result={result}
            selectAttachment={selectAttachment}
            onHover={setHoverIndex}
            // If clickable, we use the provided selectAttachment function; otherwise override with a no-op
            onClick={clickable ? undefined : noop}
          >
            {optionContent}
          </AttachDropdownOptionContainer>
        );
      })}
    </>
  );
}
AttachDropdownOptionsMenu.propTypes = {
  results: PropTypes.array.isRequired,
  getRenderOptionInfo: PropTypes.func.isRequired,
  selectAttachment: PropTypes.func.isRequired,
  hoverIndex: PropTypes.number.isRequired,
  setHoverIndex: PropTypes.func.isRequired,
  refs: PropTypes.array.isRequired,
  height: PropTypes.oneOf(Object.values(OptionHeight)).isRequired,
};

export default AttachDropdownOptionsMenu;
